import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader } from './ui/PageHeader';
import apiService from '../services/ApiService';
import Button from './ui/Button';
import type { HomeTestimonial } from './home/homeTypes';

type FormState = {
  quote: string;
  author_name: string;
  author_handle: string;
  vehicle_label: string;
  rating: number;
  avatar_url: string;
  sort_order: number;
  status: 'draft' | 'active' | 'archived';
  is_placeholder: boolean;
};

const emptyForm = (): FormState => ({
  quote: '',
  author_name: '',
  author_handle: '',
  vehicle_label: '',
  rating: 5,
  avatar_url: '',
  sort_order: 0,
  status: 'active',
  is_placeholder: false,
});

export const AdminTestimonials: React.FC = () => {
  const [items, setItems] = useState<HomeTestimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.adminListTestimonials('all');
      setItems(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load testimonials');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const startEdit = (item: HomeTestimonial) => {
    setEditingId(item.id);
    setForm({
      quote: item.quote || '',
      author_name: item.author_name || '',
      author_handle: item.author_handle || '',
      vehicle_label: item.vehicle_label || '',
      rating: item.rating ?? 5,
      avatar_url: item.avatar_url || '',
      sort_order: item.sort_order ?? 0,
      status: (item.status as FormState['status']) || 'active',
      is_placeholder: Boolean(item.is_placeholder),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm());
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.quote.trim() || !form.author_name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        quote: form.quote.trim(),
        author_name: form.author_name.trim(),
        author_handle: form.author_handle.trim() || null,
        vehicle_label: form.vehicle_label.trim() || null,
        rating: form.rating,
        avatar_url: form.avatar_url.trim() || null,
        sort_order: form.sort_order,
        status: form.status,
        is_placeholder: form.is_placeholder,
        source: form.is_placeholder ? 'seed' : 'manual',
      };
      if (editingId) {
        await apiService.adminUpdateTestimonial(editingId, payload);
      } else {
        await apiService.adminCreateTestimonial(payload);
      }
      cancelEdit();
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (id: number) => {
    try {
      await apiService.adminArchiveTestimonial(id);
      if (editingId === id) cancelEdit();
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to archive');
    }
  };

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <PageHeader
          eyebrow="Admin"
          title="Home testimonials"
          description="Social-proof blurbs on the public homepage."
        />
        <p className="text-gray-400 text-sm mt-1">
          Seed placeholders ship with the migration. Replace them with real builder quotes when you have permission,
          then clear the placeholder flag so they read as live social proof.
        </p>
      </div>

      {error ? (
        <div role="alert" className="rounded-lg border border-red-800 bg-red-950/30 p-4 text-red-300 text-sm">
          {error}
        </div>
      ) : null}

      <form onSubmit={submit} className="form-section space-y-3">
        <p className="text-sm text-gray-300 font-medium">
          {editingId ? `Editing #${editingId}` : 'Add testimonial'}
        </p>
        <label className="block space-y-1.5">
          <span className="text-xs text-gray-400">Quote</span>
          <textarea
            value={form.quote}
            onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
            rows={3}
            required
            className="input"
          />
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block space-y-1.5">
            <span className="text-xs text-gray-400">Author name</span>
            <input
              type="text"
              value={form.author_name}
              onChange={(e) => setForm((f) => ({ ...f, author_name: e.target.value }))}
              required
              className="input"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs text-gray-400">Handle (optional)</span>
            <input
              type="text"
              value={form.author_handle}
              onChange={(e) => setForm((f) => ({ ...f, author_handle: e.target.value }))}
              placeholder="builder_handle"
              className="input"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs text-gray-400">Vehicle label</span>
            <input
              type="text"
              value={form.vehicle_label}
              onChange={(e) => setForm((f) => ({ ...f, vehicle_label: e.target.value }))}
              placeholder="'19 GR Supra"
              className="input"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs text-gray-400">Rating (1-5)</span>
            <input
              type="number"
              min={1}
              max={5}
              value={form.rating}
              onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) || 5 }))}
              className="input"
            />
          </label>
          <label className="block space-y-1.5 sm:col-span-2">
            <span className="text-xs text-gray-400">Avatar URL (optional)</span>
            <input
              type="url"
              value={form.avatar_url}
              onChange={(e) => setForm((f) => ({ ...f, avatar_url: e.target.value }))}
              className="input"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs text-gray-400">Sort order</span>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setForm((f) => ({ ...f, sort_order: Number(e.target.value) || 0 }))}
              className="input"
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs text-gray-400">Status</span>
            <select
              value={form.status}
              onChange={(e) =>
                setForm((f) => ({ ...f, status: e.target.value as FormState['status'] }))
              }
              className="input"
            >
              <option value="active">Active</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={form.is_placeholder}
            onChange={(e) => setForm((f) => ({ ...f, is_placeholder: e.target.checked }))}
            className="checkbox-control"
          />
          Placeholder (seed / temporary copy)
        </label>
        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" loading={saving}>
            {editingId ? 'Save changes' : 'Add testimonial'}
          </Button>
          {editingId ? (
            <Button type="button" variant="secondary" onClick={cancelEdit}>
              Cancel
            </Button>
          ) : null}
        </div>
      </form>

      {loading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 text-sm">
          No testimonials yet. Run the home_testimonials migration, or add one above.
        </p>
      ) : (
        <ul className="divide-y divide-white/[0.06] border border-white/[0.08] rounded-xl overflow-hidden">
          {items.map((item) => (
            <li key={item.id} className="px-4 py-3 flex items-start justify-between gap-3 bg-[#0c0c0c]/50">
              <div className="min-w-0">
                <p className="text-white font-medium text-sm">
                  {item.author_name}
                  {item.is_placeholder ? (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-amber-400/90">
                      Placeholder
                    </span>
                  ) : null}
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-gray-500">
                    {item.status}
                  </span>
                </p>
                <p className="text-gray-400 text-xs mt-1 line-clamp-2">&ldquo;{item.quote}&rdquo;</p>
                {item.vehicle_label ? (
                  <p className="text-gray-500 text-xs mt-1">{item.vehicle_label}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Button type="button" variant="secondary" size="sm" onClick={() => startEdit(item)}>
                  Edit
                </Button>
                {item.status !== 'archived' ? (
                  <Button type="button" variant="danger" size="sm" onClick={() => void archive(item.id)}>
                    Archive
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AdminTestimonials;
